import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import RootErrorRecovery from "./components/RootErrorRecovery.jsx";
import { BrowserRouter } from "react-router-dom";
import EntryRouter, { EntryLoading } from "./components/EntryRouter.jsx";
import { lazyWithRetry } from "./utils/lazyWithRetry.js";
const ClientApp = lazyWithRetry(() => import("./components/ClientApp.jsx"), "공유 대시보드");
import { AuthProvider } from "./contexts/AuthContext.jsx";
import "./index.css";
import { initTheme } from "./utils/theme.js";
import { purgeLegacyProviderKeys } from "./utils/safeStorage.js";

// ── C6 P1: 레거시 provider 키 제거 (Sentry init·렌더보다 먼저, 동기) ──
// 이전 버전이 localStorage 에 저장한 Anthropic/Gemini 키를 남겨두면, 서버 env 로
// 권위를 옮긴 뒤에도 브라우저에 장수 자격이 그대로 남는다. 부팅 최초 시점에 지운다.
purgeLegacyProviderKeys();

// ── Sentry frontend 결함 적재 (DSN 부재 시점에 init skip — 운영 차단) ──
// 광고주 공유 링크(/client 접두)에서는 init하지 않는다(2026-10-02 광고주 B9 편집 트랙 S1).
// 공유 토큰이 경로에 있어 pageload 트랜잭션 이름·fetch span·오류 replay URL로 Sentry에 실린다.
// 편집 허용 링크에서는 그 토큰이 쓰기 자격이 되므로 제3자 전송 경로 자체를 만들지 않는다.
// 회귀 핀: tests/share-link-telemetry.test.mjs, prod 번들 검사: test:share-telemetry:preview.
const SHARE_PATH = /^\/client(\/|$)/i.test(window.location.pathname);
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (import.meta.env.PROD && SENTRY_DSN && !SHARE_PATH) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

function SentryFallback({ error } = {}) {
  return <RootErrorRecovery error={error} />;
}

const CLIENT_TOKEN_RE =
  /^\/client\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const clientMatch = window.location.pathname.match(CLIENT_TOKEN_RE);

// Phase A Task 3: 테마 초기화 (localStorage 저장값 → DOM data-theme 동기화). ReactDOM.createRoot 호출 직전.
initTheme();

if (clientMatch) {
  ReactDOM.createRoot(document.getElementById("root")).render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={SentryFallback}>
        <Suspense fallback={<EntryLoading/>}><ClientApp token={clientMatch[1]} /></Suspense>
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  );
} else {
  ReactDOM.createRoot(document.getElementById("root")).render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={SentryFallback}>
        <BrowserRouter>
          <AuthProvider>
            <EntryRouter />
          </AuthProvider>
        </BrowserRouter>
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  );
}
